import validate, { capitalize, isString } from 'validate.js/validate';
import { removeByType, removeFalsyErrors } from 'lib/removeTypeName';
import { Collection, getIn } from 'immutable';

validate.validators.array = (arrayItems, itemConstraints, key, attributes) => {
	const { isRequired, fieldName, ...constraints } = itemConstraints;
	if (isRequired === false && !arrayItems) return null;
	if (isRequired === true && !arrayItems.length) return `^A ${fieldName || ''} is required`;
	if (typeof isRequired === 'string' && RegExp('if:(.*)').test(isRequired)) {
		const rules = isRequired
			.split(':')[1]
			.split(',')
			.reduce((acc, cur) => {
				const [path, value] = cur.split('=');
				try {
					acc[path] = JSON.parse(value);
				} catch {
					acc[path] = value;
				}
				return acc;
			}, {});
		if (
			Object.entries(rules)
				.map(([path, value]) => {
					return getIn(attributes, path.split('.')) === value;
				})
				.every((entry) => entry === true) &&
			!(arrayItems || []).length
		)
			return `^${fieldName || ''} is required`;
	}
	const arrayItemErrors = (arrayItems || []).reduce((errors, item, index) => {
		const error = validate(isString(item) ? { value: item } : item, constraints);
		//if error is undefined we return a unique falsy variable because validate js will make the errors array unique and  we'll lose errors order
		errors[index] = error || -index;
		return errors;
	}, []);
	return arrayItemErrors.length ? arrayItemErrors : null;
};

validate.validators.color = function (value) {
	const isRGBA = ['rgba', 'rgba__typename', 'abgr'].includes(Object.keys(value).join(''));
	if (!isRGBA) return 'must be an rgba object';

	const rules = {
		r: (int) => Number.isInteger(int) && int >= 0 && int <= 255,
		g: (int) => Number.isInteger(int) && int >= 0 && int <= 255,
		b: (int) => Number.isInteger(int) && int >= 0 && int <= 255,
		a: (float) => typeof float === 'number' && float >= 0 && float <= 1,
	};
	const valuesAreValid = Object.entries(value).reduce((acc, [key, val]) => {
		if (key === '__typename') return acc;
		acc += rules[key](val) ? 0 : -1;
		return acc;
	}, 0);
	return valuesAreValid < 0 ? 'is not valid' : null;
};

export const Field = ({ required, min, max, inArray, type, name, debug } = {}) => {
	const output = removeByType({
		presence: Value(required)({
			allowEmpty: false,
			message: Value(name)(`^${capitalize(name)} can't be blank`),
		}),
		length: Value(type === 'string' && (min || max))({
			minimum: required ? min || 1 : 0,
			tooShort: Value(min && name)(`${capitalize(name)} is too short (minimum is ${min} characters)`),
			maximum: max,
			tooLong: Value(max && name)(`${capitalize(name)} is too long (maximum is ${max} characters)`),
		}),
		inclusion: Value(inArray)({
			within: inArray,
			message: Value(name)(`^${capitalize(name)} value is not valid`),
		}),
		numericality: Value(['integer', 'float'].includes(type))({
			onlyInteger: type === 'integer',
			greaterThanOrEqualTo: Value(min)(),
			lessThanOrEqualTo: Value(max)(),
			strict: true,
			notValid: Value(name)(`^${capitalize(name)} value is not valid`, `must be a valid ${type}`),
			notGreaterThanOrEqualTo: Value(name)(`^${capitalize(name)}  must be greater than or equal to ${min}`),
			notLessThanOrEqualTo: Value(name)(`^${capitalize(name)}  must be less than or equal to ${max}`),
		}),
		type: type !== 'float' ? type : undefined,
		// type: Value(type && type !== 'float', type),
	});
	if (!!debug) console.log(output);
	return output;
};
const Value = (condition) => (value = condition, defaultValue = undefined) => (!!condition ? value : defaultValue);

export const Integer = ({ required = true, ...options } = {}) => Field({ required, type: 'integer', ...options });
export const Float = ({ required = true, ...options } = {}) => Field({ required, type: 'float', ...options });
export const Text = ({ required = true, ...options } = {}) => Field({ required, type: 'string', ...options });
export const Bool = ({ required = true, ...options } = {}) => Field({ required, type: 'boolean', ...options });
export const Num = ({ required = true, ...options } = {}) => Field({ required, type: 'number', ...options });

export const Run = (state, specific, { errors, setErrors, constraints }, silent = false) => {
	let filteredConstraints = Collection(constraints)
		.filter((value, key) => (specific.length ? specific.includes(key) : true))
		.toObject();
	let newErrors = validate(state, filteredConstraints);
	let oldErrors = Object.assign({}, errors);
	let outputErrors = newErrors;
	if (specific.length && Object.keys(oldErrors).length) {
		Object.keys(oldErrors).forEach((key) => {
			if ((!newErrors && specific.includes(key)) || !specific.includes(key)) {
				//no errors, we should remove the specified keys from old errors
				delete oldErrors[key];
			} else if (specific.includes(key) && !!newErrors[key]) {
				// new errors, we should replace old errors with new ones
				oldErrors[key] = newErrors[key];
			}
		});
		outputErrors = oldErrors;
	}
	if (!silent) setErrors(outputErrors || {});
	return removeFalsyErrors(outputErrors);
};

export default validate;
